import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import { Button, Label, TextInput } from './ui';

const Login: React.FC = () => {
  const [searchParams] = useSearchParams();
  const registered = searchParams.get('registered') === '1';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const { colors } = useTheme();
  const { login } = useAuth();

  const validateEmail = (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const validateForm = (): boolean => {
    let isValid = true;
    setEmailError('');
    setPasswordError('');

    if (!email) {
      setEmailError('Email is required');
      isValid = false;
    } else if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      isValid = false;
    }

    if (!password) {
      setPasswordError('Password is required');
      isValid = false;
    } else if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await login({ email, password });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={cn('min-h-screen flex flex-col lg:flex-row', colors.background)}
      style={
        {
          '--panel-deep': '#0a1424',
          '--panel-mid': '#143052',
          '--panel-accent': '#6b8fb8',
          '--panel-accent-dim': 'rgba(107, 143, 184, 0.35)',
          '--login-font': "'Fraunces', Georgia, 'Times New Roman', serif",
        } as React.CSSProperties
      }
    >
      <style>{`
        @keyframes split-in {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes brand-glow {
          0%, 100% { opacity: 0.55; }
          50% { opacity: 0.85; }
        }
        .split-in { animation: split-in 0.6s cubic-bezier(0.22, 1, 0.36, 1) both; }
        .split-in-late { animation: split-in 0.6s cubic-bezier(0.22, 1, 0.36, 1) 0.12s both; }
        .brand-glow { animation: brand-glow 12s ease-in-out infinite; }
      `}</style>

      {/* Brand half */}
      <aside
        className="relative flex lg:w-1/2 flex-col justify-between overflow-hidden px-8 py-10 sm:px-12 lg:px-14 xl:px-20 text-white min-h-[40vh] lg:min-h-screen"
        style={{ background: 'linear-gradient(165deg, var(--panel-deep) 0%, #0f1f38 48%, var(--panel-mid) 100%)' }}
      >
        <div
          className="brand-glow pointer-events-none absolute -top-24 -left-16 h-80 w-80 rounded-full blur-3xl"
          aria-hidden
          style={{ background: 'radial-gradient(circle, rgba(107,143,184,0.18), transparent 70%)' }}
        />
        <div
          className="pointer-events-none absolute bottom-0 right-0 h-64 w-64 rounded-full blur-3xl opacity-40"
          aria-hidden
          style={{ background: 'radial-gradient(circle, rgba(30,80,120,0.5), transparent 70%)' }}
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          aria-hidden
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)',
            backgroundSize: '56px 56px',
            maskImage: 'linear-gradient(180deg, black 0%, transparent 85%)',
          }}
        />
        {/* Vertical accent seam */}
        <div
          className="pointer-events-none absolute top-0 bottom-0 right-0 w-px hidden lg:block"
          aria-hidden
          style={{
            background:
              'linear-gradient(180deg, transparent 8%, var(--panel-accent-dim) 40%, var(--panel-accent) 50%, var(--panel-accent-dim) 60%, transparent 92%)',
          }}
        />

        <div className="relative">
          <div className="flex items-center gap-3">
            <img
              src={`${process.env.PUBLIC_URL}/atlas-logo.svg`}
              alt=""
              className="h-9 w-9 rounded-lg ring-1 ring-white/15 shadow-lg"
              style={{ boxShadow: '0 8px 24px rgba(166, 122, 77, 0.35)' }}
            />
            <span
              className="inline-flex items-center rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
              style={{
                background: 'rgba(107,143,184,0.12)',
                color: 'var(--panel-accent)',
                border: '1px solid rgba(107,143,184,0.28)',
              }}
            >
              Office
            </span>
          </div>

          <p
            className="mt-10 text-[clamp(3rem,6vw,4.5rem)] font-semibold tracking-[-0.03em] leading-none"
            style={{ fontFamily: 'var(--login-font)' }}
          >
            ATLAS
          </p>
          <div className="mt-4 flex items-center gap-3">
            <span
              className="h-px w-10 shrink-0"
              style={{ background: 'var(--panel-accent)' }}
              aria-hidden
            />
            <p className="text-xs uppercase tracking-[0.28em] text-[color:var(--panel-accent)]">
              SCMS · Shore office
            </p>
          </div>
        </div>

        <div className="relative mt-12 lg:mt-0 max-w-md">
          <h1
            className="text-[1.35rem] sm:text-[1.65rem] font-medium leading-snug text-white/95"
            style={{ fontFamily: 'var(--login-font)' }}
          >
            Purchasing, HR, and finance — one shore workspace.
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-slate-400">
            Sign in to continue to the office portal.
          </p>
        </div>

        <p className="relative mt-12 lg:mt-0 text-xs tracking-wide text-slate-500">
          Authorized personnel only
        </p>
      </aside>

      {/* Form half */}
      <main
        className="relative flex flex-1 lg:w-1/2 flex-col justify-center px-5 py-10 sm:px-10 lg:px-14 xl:px-20"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 70% 20%, rgba(107,143,184,0.06), transparent 55%)',
        }}
      >
        <div
          className={cn(
            'split-in w-full max-w-md mx-auto lg:mx-0 rounded-2xl border p-7 sm:p-9 shadow-xl shadow-slate-900/[0.06] dark:shadow-black/30',
            colors.surface,
            colors.border,
          )}
        >
          <div className="mb-7">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#2c4a6e]/80 dark:text-[#9bb4d0]/90">
              Welcome back
            </p>
            <h2
              className={cn('mt-2 text-2xl sm:text-[1.75rem] font-semibold tracking-tight', colors.text)}
              style={{ fontFamily: 'var(--login-font)' }}
            >
              Sign in
            </h2>
            <p className={cn('mt-1.5 text-sm', colors.textMuted)}>
              Enter your office credentials to continue.
            </p>
            <div
              className="mt-5 h-px w-full"
              style={{
                background:
                  'linear-gradient(90deg, rgba(61,90,128,0.55), rgba(61,90,128,0.12), transparent)',
              }}
              aria-hidden
            />
          </div>

          <form className="split-in-late space-y-5" onSubmit={handleSubmit} noValidate>
            {registered && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 dark:border-emerald-800/50 dark:bg-emerald-950/40">
                <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                  Account created. You can now sign in.
                </p>
              </div>
            )}

            {error && (
              <div
                className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 dark:border-red-900/50 dark:bg-red-950/40"
                role="alert"
              >
                <div className="flex items-start gap-2.5">
                  <svg
                    className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <Label htmlFor="email" className="mb-1.5">
                  Email address
                </Label>
                <TextInput
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError('');
                  }}
                  error={emailError}
                  size="lg"
                />
              </div>

              <div>
                <Label htmlFor="password" className="mb-1.5">
                  Password
                </Label>
                <TextInput
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (passwordError) setPasswordError('');
                  }}
                  error={passwordError}
                  showClearButton={false}
                  size="lg"
                />
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 pt-1">
              <label className={cn('flex items-center gap-2 text-sm cursor-pointer', colors.textSecondary)}>
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-[#3d5a80] focus:ring-[#3d5a80] dark:border-slate-600"
                />
                Remember me
              </label>
              <button
                type="button"
                onClick={() => console.log('Forgot password clicked')}
                className="text-sm font-medium text-[#2c4a6e] hover:text-[#3d5a80] dark:text-[#9bb4d0] dark:hover:text-[#b0c5db]"
              >
                Forgot password?
              </button>
            </div>

            <Button
              type="submit"
              variant="accent"
              size="lg"
              disabled={isLoading}
              loading={isLoading}
              className="w-full !bg-[#3d5a80] hover:!bg-[#2c4a6e] focus:!ring-[#3d5a80] shadow-md shadow-slate-900/15"
            >
              {isLoading ? 'Signing in…' : 'Sign in to office'}
            </Button>
          </form>
        </div>

        <p className={cn('split-in-late mt-8 text-center lg:text-left text-xs max-w-md mx-auto lg:mx-0', colors.textMuted)}>
          © {new Date().getFullYear()} ATLAS SCMS · Shore office · Authorized use only
        </p>
      </main>
    </div>
  );
};

export default Login;
